<script>
  import { Link } from "svelte-routing";
  import { getContext } from "svelte";
  import get from "lodash/get";

  import CircleCheckIcon from "@local/assets/icons/circle-check-filled.svg";
  import CircleWarningIcon from "@local/assets/icons/circle-warning-filled.svg";
  import XIcon from "@local/assets/icons/x.svg";

  import SidebarSection from "./SidebarSection.svelte";
  import NewLocationButtons from "./NewLocationButtons.svelte";
  import formatDims from "@local/extensions/formatters/format-dims.js";
  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import liteDrawingMini from "@local/extensions/drawing/lite-drawing-mini.js";
  import DimensionLinearIcon from "@local/assets/icons/dimension-linear.svg";
  import DimensionRectangularIcon from "@local/assets/icons/dimension-rectangular.svg";
  import { selectedLocations, selectedDimensions } from "#src/stores/ui.js";

  export let group;
  export let locations;
  export let itemsWithLocs;
  export let documentNotesWithLocs;
  export let locationCounts;
  export let collectionsWithLocs;
  export let typesWithLocs;
  export let selectedDimensionsList = [];
  export let single = false;

  const typeColors = getContext("typeColors");

  function description(item) {
    const mark = item.mark || "";
    const labelBy = item.is_collection
      ? $group.data.settings.collections_label_by
      : $group.data.settings.label_by;
    const label = (labelBy && get(item, labelBy)) || "";

    let textLbl = label && label !== mark ? `${mark} - ${label}` : mark;

    if (item.width && item.height) {
      return `${textLbl} - ${formatDims(item, $group.data.settings)}`;
    } else {
      return textLbl;
    }
  }

  function deselectItem(id) {
    const toDeselect = locations.filter((loc) => loc.record_id === id).map((loc) => loc.id);
    selectedLocations.deselect(...toDeselect);
  }

  function deselectDim(id) {
    selectedDimensions.deselect(id);
  }
</script>

<SidebarSection>
  <div class="px-2 space-y-2">
    {#if itemsWithLocs.length}
      {#if !single}
        <div class="font-bold">Items</div>
      {/if}
      <div class="space-y-1">
        {#each itemsWithLocs as item (item.id)}
          {@const typeColor = $typeColors[item.type_id]}
          <div class="flex justify-between items-center item-row">
            <div class="flex gap-3 items-center">
              <NewLocationButtons record_type="item" record_id={item.id} on:add-location on:clone-location />
              <Link to={`items/${item.id}`} class="truncate">
                <div class="flex gap-2">
                  <div class="w-4 h-4 relative flex-none">
                    {#key item}
                      <Viewport
                        padding={1}
                        drawing={liteDrawingMini(item, { typeColor })}
                        width={16}
                        height={16} />
                    {/key}
                  </div>
                  <div class="truncate">
                    {description(item)}
                  </div>
                </div>
              </Link>
              {#if locationCounts[item.id] > 1}
                <div>({locationCounts[item.id]})</div>
              {/if}
            </div>
            <button class="deselect-button" on:click={() => deselectItem(item.id)}>
              <XIcon />
            </button>
          </div>
        {/each}
      </div>
    {/if}
    {#if collectionsWithLocs.length}
      {#if !single}
        <div class="font-bold">Openings</div>
      {/if}
      <div class="space-y-1">
        {#each collectionsWithLocs as coll (coll.id)}
          <div class="flex justify-between items-center item-row">
            <div class="flex gap-3 items-center">
              <NewLocationButtons
                record_type="collection"
                record_id={coll.id}
                on:add-location
                on:clone-location />
              <Link to={`openings/${coll.id}`} class="truncate">
                <div class="flex gap-2">
                  <div class="w-4 h-4 relative flex-none">
                    {#key coll}
                      <Viewport padding={1} drawing={liteDrawingMini(coll)} width={16} height={16} />
                    {/key}
                  </div>
                  <div class="truncate">
                    {description(coll)}
                  </div>
                </div>
              </Link>
              {#if locationCounts[coll.id] > 1}
                <div>({locationCounts[coll.id]})</div>
              {/if}
            </div>
            <button class="deselect-button" on:click={() => deselectItem(coll.id)}>
              <XIcon />
            </button>
          </div>
        {/each}
      </div>
    {/if}
    {#if typesWithLocs.length}
      {#if !single}
        <div class="font-bold">Types</div>
      {/if}
      <div class="space-y-1">
        {#each typesWithLocs as type (type.id)}
          <div class="flex justify-between items-center item-row">
            <div class="flex gap-3 items-center">
              <NewLocationButtons record_type="type" record_id={type.id} on:add-location on:clone-location />
              <div class="flex gap-2 items-center overflow-hidden">
                <Link to={`types/${type.id}`} class="truncate">{type.mark} - {type.name}</Link>
                {#if locationCounts[type.id] > 1}
                  <div>({locationCounts[type.id]})</div>
                {/if}
              </div>
              {#if type.approval_status !== "none"}
                <div class="grow flex justify-end items-center gap-1">
                  {#if type.approval_status === "approved"}
                    <div class="text-green-500">
                      <CircleCheckIcon />
                    </div>
                  {:else if type.approval_status === "flagged"}
                    <div class="text-red-500">
                      <CircleWarningIcon />
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
            <button class="deselect-button" on:click={() => deselectItem(type.id)}>
              <XIcon />
            </button>
          </div>
        {/each}
      </div>
    {/if}
    {#if documentNotesWithLocs.length}
      {#if !single}
        <div class="font-bold">Notes</div>
      {/if}
      <div class="space-y-1">
        {#each documentNotesWithLocs as note (note.id)}
          <div class="flex justify-between items-center item-row">
            <div class="flex gap-3 items-center">
              <NewLocationButtons
                record_type="document_note"
                record_id={note.id}
                on:add-location
                on:clone-location />
              <div class="flex gap-2 items-center overflow-hidden">
                <div class="truncate">{note.mark} - {note.title}</div>
                {#if locationCounts[note.id] > 1}
                  <div>({locationCounts[note.id]})</div>
                {/if}
              </div>
              {#if note.approval_status !== "none"}
                <div class="grow flex justify-end items-center gap-1">
                  {#if note.approval_status === "approved"}
                    <div class="text-green-500">
                      <CircleCheckIcon />
                    </div>
                  {:else if note.approval_status === "flagged"}
                    <div class="text-red-500">
                      <CircleWarningIcon />
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
            <button class="deselect-button" on:click={() => deselectItem(note.id)}>
              <XIcon />
            </button>
          </div>
        {/each}
      </div>
    {/if}
    {#if selectedDimensionsList.length}
      <div class="font-bold">Dimensions</div>
      <div class="space-y-1">
        {#each selectedDimensionsList as dim (dim.id)}
          <div class="flex justify-between items-center item-row">
            <div class="flex gap-3 items-center">
              {#if dim.type === "aligned"}
                <DimensionLinearIcon />
              {:else if dim.type === "rectangular"}
                <DimensionRectangularIcon />
              {/if}
              <div class="grow truncate">
                {#if dim.type === "aligned"}
                  L: {dim.data.values.length}
                {:else if dim.type === "rectangular"}
                  W: {dim.data.values.width}&nbsp;&nbsp;H: {dim.data.values.height}
                {/if}
              </div>
            </div>
            <button class="deselect-button" on:click={() => deselectDim(dim.id)}>
              <XIcon />
            </button>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</SidebarSection>

<style lang="scss">
  .item-row {
    .deselect-button {
      @apply invisible;
    }

    &:hover {
      .deselect-button {
        @apply visible;
      }
    }
  }
</style>
